import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import { groupBy } from 'lodash';
import { useEffect, useState } from 'react';
import {
    BackButton,
    BackButtonIconType,
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H5Emphasized,
    isWeb,
    LandmarkIcon,
    SafeAreaView,
    ScrollView,
    Separator,
    Spinner,
    tokens,
    useWindowDimensions,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import AccountLinkingSheet from '../../holistic/components/AccountLinkingSheet';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { OpenBillsAdminSheetButton } from '../admin/BillsAdminSheet';
import useGetBillDiscoveryReview from '../hooks/useGetBillDiscoveryReview';
import {
    billDiscoveryReviewAccountsAtom,
    billsToReviewAtom,
} from '../state/BillsAtoms';
import type { _Bill } from '../types/Bill';
import type { BillDiscoveryReviewAccount } from '../types/BillDiscoveryAccount';
import { BillAccountRow } from './common/BillRows';

const BillsDiscoveryAccounts = ({
    screenContext,
}: {
    screenContext: 'initialDiscovery' | 'addMoreBills';
}) => {
    LogRocketFacade.tagPage('BillsDiscoveryAccounts');

    const dimensions = useWindowDimensions();

    const user = useAuthedUser();

    const storeBillDiscoveryReviewAccounts = useSetAtom(
        billDiscoveryReviewAccountsAtom,
    );
    const storeBillsToReview = useSetAtom(billsToReviewAtom);

    const [isLinkingSheetOpen, setIsLinkingSheetOpen] = useState(false);

    const [selectableAccounts, setSelectableAccounts] = useState<
        BillDiscoveryReviewAccount[]
    >([]);

    const {
        data: fetchedBillDiscoveryReview,
        isLoading: isLoadingBillDiscoveryReview,
    } = useGetBillDiscoveryReview(user?.membership?.id ?? '');

    useEffect(() => {
        if (fetchedBillDiscoveryReview.billAccounts.length > 0) {
            storeBillsToReview(fetchedBillDiscoveryReview.bills ?? []);
            storeBillDiscoveryReviewAccounts(
                fetchedBillDiscoveryReview.billAccounts,
            );
            setSelectableAccounts(
                fetchedBillDiscoveryReview.billAccounts
                    .filter(
                        (account) =>
                            !account.discoveryResultsImportedAt &&
                            !account.discoveryReviewCompletedAt,
                    )
                    .map((account) => {
                        account.isSelectedForCurrentDiscovery = true;
                        return account;
                    })
                    // sort by institution name ASC, then account name ASC
                    .sort((a, b) => {
                        return (
                            a.institutionName.localeCompare(
                                b.institutionName,
                            ) || a.name.localeCompare(b.name)
                        );
                    }),
            );
        }
    }, [fetchedBillDiscoveryReview]);

    useEffect(() => {
        const { bills: allBills } = fetchedBillDiscoveryReview;
        if (selectableAccounts.length && allBills.length > 0) {
            const billsByAccount = groupBy(allBills, 'accountId');
            const accountBills = selectableAccounts.reduce<
                Array<Required<_Bill>>
            >((acc, account) => {
                const bills = billsByAccount[account.accountId];
                if (bills && account.isSelectedForCurrentDiscovery) {
                    acc.push(...bills);
                }
                return acc;
            }, []);
            storeBillsToReview(accountBills);
        } else {
            storeBillsToReview([]);
        }
    }, [selectableAccounts]);

    const storeAccountSelections = () => {
        storeBillDiscoveryReviewAccounts((prevAccounts) => {
            return prevAccounts.map((prevAccount) => {
                const matchingAccount = selectableAccounts.find(
                    (selectableAccount) =>
                        selectableAccount.accountId === prevAccount.id,
                );
                return {
                    ...prevAccount,
                    isSelectedForCurrentDiscovery:
                        matchingAccount?.isSelectedForCurrentDiscovery ?? false,
                };
            });
        });
    };

    const enableSubmit =
        selectableAccounts.filter((a) => a.isSelectedForCurrentDiscovery)
            .length > 0;

    return (
        <SafeAreaView
            style={{
                flex: 1,
            }}
        >
            <YStack
                height="100%"
                paddingVertical={isWeb ? 24 : undefined}
                justifyContent="space-between"
                maxWidth={700}
                alignSelf="center"
            >
                <YStack flex={1} paddingHorizontal={24}>
                    <BackButton
                        customBackFn={() => {
                            router.navigate(
                                screenContext === 'addMoreBills'
                                    ? '/bills/bills-overview'
                                    : screenContext === 'initialDiscovery'
                                      ? '/bills/bills-discovery'
                                      : '/',
                            );
                        }}
                        iconType={BackButtonIconType.ARROW_LEFT}
                    />
                    <H5Emphasized paddingTop={28} paddingBottom={12}>
                        We scanned these accounts to find your bills
                    </H5Emphasized>
                    <BodyMediumRegular color="$gray1000" paddingBottom={24}>
                        Make sure to link any other accounts you use to pay
                        bills and subscriptions. We&apos;ll notify you of any
                        additional recurring expenses we may find.
                    </BodyMediumRegular>
                    <XStack
                        paddingVertical={12}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <BodyMediumRegular color="$gray1000">
                            Accounts
                        </BodyMediumRegular>
                        <BodyMediumRegular color="$gray1000">
                            Expenses detected
                        </BodyMediumRegular>
                    </XStack>
                    <ScrollView
                        marginTop={4}
                        showsVerticalScrollIndicator={isWeb}
                    >
                        {isLoadingBillDiscoveryReview ? (
                            <Spinner marginTop={64} />
                        ) : (
                            <YStack>
                                <Separator />
                                {selectableAccounts
                                    ?.sort((acctA, acctB) => {
                                        return (
                                            (acctB?.discoveredBillsCount ?? 0) -
                                            (acctA?.discoveredBillsCount ?? 0)
                                        );
                                    })
                                    .map((account, i) => {
                                        return (
                                            <View key={i}>
                                                <XStack
                                                    paddingVertical={16}
                                                    justifyContent="space-between"
                                                >
                                                    <BillAccountRow
                                                        account={account}
                                                        viewType="account_selection"
                                                    />
                                                    <BodyLargeEmphasized>
                                                        {
                                                            account.discoveredBillsCount
                                                        }
                                                    </BodyLargeEmphasized>
                                                </XStack>
                                                <Separator />
                                            </View>
                                        );
                                    })}
                                <YStack paddingVertical={24}>
                                    <Button
                                        icon={LandmarkIcon}
                                        variant={ButtonVariant.SECONDARY}
                                        size={ButtonSize.LARGE}
                                        text="Link an account"
                                        onPress={() => {
                                            setIsLinkingSheetOpen(true);
                                        }}
                                    />
                                </YStack>
                            </YStack>
                        )}
                    </ScrollView>
                    <LinearGradient
                        colors={[
                            'rgba(255, 255, 255, 0)',
                            tokens.color.light.brand.white,
                        ]}
                        style={{
                            height: 20,
                            width: dimensions.width,
                            position: 'absolute',
                            bottom: 0,
                        }}
                        pointerEvents="none"
                    />
                </YStack>
                <Separator />
                <YStack
                    paddingTop={16}
                    paddingBottom={isWeb ? 16 : undefined}
                    paddingHorizontal={24}
                >
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Review"
                        disabled={!enableSubmit}
                        onPress={() => {
                            storeAccountSelections();
                            router.navigate('/bills/bills-discovery-review');
                        }}
                    />
                </YStack>
            </YStack>
            <OpenBillsAdminSheetButton />
            <AccountLinkingSheet
                isOpen={isLinkingSheetOpen}
                onClose={() => {
                    setIsLinkingSheetOpen(false);
                }}
            />
        </SafeAreaView>
    );
};

export default BillsDiscoveryAccounts;
